import { Link } from "@tanstack/react-router";
import {
  Check,
  CheckCircle2,
  AlertTriangle,
  Layers,
  ArrowRight,
  ShieldCheck,
  Quote,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { StartupPricingCallout } from "./StartupPricingCallout";
import { LeadMagnet } from "./LeadMagnet";
import { type IndustryDetail } from "@/lib/industry-details";
import { withUTM } from "@/lib/utm";

export function IndustryDetailPage({ data }: { data: IndustryDetail }) {
  const ctaWithUtm = withUTM(data.ctaHref, {
    industry: data.slug,
    source: "industry_page_bottom_cta",
    page: `industries_${data.slug}`,
  });

  return (
    <>
      {/* 1. Hero Section */}
      <section className="px-6 pb-16 pt-24 md:pt-32 border-b border-brand-navy/5 bg-gradient-to-b from-brand-surface/60 to-background">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-accent/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-navy">
                  <span className="size-2 rounded-full bg-brand-accent animate-pulse" />
                  Industry Focus
                </div>

                <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
                  {data.title}
                </h1>

                <p className="mt-6 max-w-2xl text-lg text-brand-navy/70 leading-relaxed">
                  {data.description}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Link
                    to="/contact"
                    search={{ industry: data.slug }}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-8 py-4 font-semibold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-brand-accent hover:shadow-xl active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
                  >
                    {data.ctaText} <ArrowRight className="size-4" />
                  </Link>

                  <a
                    href="#pain-points"
                    className="rounded-xl border border-brand-navy/15 px-6 py-4 font-semibold text-brand-navy transition-[background-color,border-color,transform] duration-150 ease-out hover:bg-brand-surface active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
                  >
                    Explore Common Pitfalls
                  </a>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-4">
              <Reveal delay={0.15}>
                <div className="rounded-3xl border border-brand-navy/10 bg-brand-navy p-8 text-background shadow-2xl">
                  <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                    Key Performance Standard
                  </span>
                  <div className="mt-4 font-display text-5xl font-extrabold text-background">
                    {data.heroStat.value}
                  </div>
                  <p className="mt-2 text-sm text-background/80 font-medium leading-relaxed">
                    {data.heroStat.label}
                  </p>

                  <div className="mt-6 border-t border-background/10 pt-6 space-y-3 text-xs text-background/70">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="size-4 text-brand-accent shrink-0" />
                      Dedicated accounting pod assigned to your sector
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="size-4 text-brand-accent shrink-0" />
                      Audit-ready GAAP workpapers from day one
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="size-4 text-brand-accent shrink-0" />
                      Month-to-month retainers • Cancel anytime
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Pain Points Table */}
      <section id="pain-points" className="px-6 py-20 bg-background">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              The Reality
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              Where standard bookkeeping breaks in this sector
            </h2>
            <p className="mt-4 text-base text-brand-navy/60 leading-relaxed">
              Off-the-shelf bookkeepers treat all transactions the same. Here is what goes wrong
              when accounting isn't tailored to your operating model:
            </p>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {data.painPoints.map((item, i) => (
              <Reveal key={item.pain} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-brand-navy/10 bg-brand-surface/50 p-8 transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out hover:border-brand-accent/40 hover:bg-background hover:shadow-xl hover:-translate-y-0.5 motion-reduce:transform-none">
                  <div className="flex items-start gap-4">
                    <div className="rounded-xl bg-destructive/10 p-2.5 text-destructive">
                      <AlertTriangle className="size-5 shrink-0" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-brand-navy">
                        {item.pain}
                      </h3>
                      <p className="mt-2 text-sm text-destructive/90 font-medium">
                        <strong>Impact:</strong> {item.impact}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 border-t border-brand-navy/10 pt-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-brand-accent">
                      How Finendra solves this:
                    </p>
                    <p className="mt-2 text-sm text-brand-navy/70 leading-relaxed flex items-start gap-2">
                      <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                      {item.solution}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Stack Diagram */}
      <section className="px-6 py-20 bg-brand-surface/40 border-y border-brand-navy/5">
        <div className="mx-auto max-w-7xl">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Integrations
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              We operate inside your existing finance stack
            </h2>
            <p className="mt-4 text-base text-brand-navy/60">
              No migrations required. We connect directly into your cloud accounts with
              least-privilege read/write access.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {data.stack.map((group, idx) => (
              <Reveal key={group.category} delay={idx * 0.08}>
                <div className="h-full rounded-2xl border border-brand-navy/10 bg-background p-6 shadow-sm">
                  <div className="flex items-center gap-2 text-brand-accent font-semibold text-xs uppercase tracking-wider">
                    <Layers className="size-4" />
                    {group.category}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {group.tools.map((tool) => (
                      <span
                        key={tool}
                        className="rounded-lg border border-brand-navy/10 bg-brand-surface px-3 py-1.5 text-xs font-bold text-brand-navy"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Deliverables Checklist */}
      <section className="px-6 py-20 bg-background">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Outputs & Cadence
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              What you receive every month
            </h2>
            <p className="mt-4 text-base text-brand-navy/60">
              Clear, scheduled financial deliverables delivered directly to your inbox and data room
              on a fixed cadence.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-8 lg:grid-cols-3">
            {data.deliverables.map((deliv, idx) => (
              <Reveal key={deliv.category} delay={idx * 0.1}>
                <div className="h-full rounded-3xl border border-brand-navy/10 bg-brand-surface/40 p-8">
                  <h3 className="font-display text-lg font-bold text-brand-navy border-b border-brand-navy/10 pb-4">
                    {deliv.category}
                  </h3>
                  <ul className="mt-6 space-y-3.5 text-sm text-brand-navy/70">
                    {deliv.items.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Check
                          className="size-4 text-brand-accent shrink-0 mt-1"
                          strokeWidth={2.5}
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Testimonial Card */}
      <section className="px-6 py-16 bg-brand-navy text-background">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <div className="relative rounded-3xl border border-background/10 bg-brand-surface/5 p-8 md:p-12 backdrop-blur-sm">
              <Quote className="size-10 text-brand-accent/40 mb-4" />
              <blockquote className="text-xl md:text-2xl font-display font-medium text-background leading-relaxed">
                "{data.proof.quote}"
              </blockquote>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background/10 pt-6">
                <div>
                  <div className="font-bold text-background text-base">{data.proof.author}</div>
                  {data.proof.stage && (
                    <div className="text-xs text-background/60 mt-0.5">
                      Stage: {data.proof.stage}
                    </div>
                  )}
                </div>

                {data.proof.plan && (
                  <span className="rounded-full bg-brand-accent/20 px-3 py-1 text-xs font-bold text-brand-accent">
                    Plan: {data.proof.plan}
                  </span>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. Pricing Table */}
      <section className="px-6 py-20 bg-background">
        <div className="mx-auto max-w-7xl">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Transparent Investment
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              Predictable monthly pricing
            </h2>
            <p className="mt-4 text-base text-brand-navy/60">
              No hourly billing. Fixed retainers based on your operational volume.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {data.pricingTiers.map((tier, idx) => (
              <Reveal key={tier.name} delay={idx * 0.08} className="h-full">
                <div
                  className={`h-full flex flex-col rounded-3xl border p-8 transition-[border-color,box-shadow,transform] duration-200 ease-out ${
                    tier.popular
                      ? "border-brand-accent bg-background shadow-2xl ring-2 ring-brand-accent/20"
                      : "border-brand-navy/10 bg-brand-surface/30"
                  }`}
                >
                  {tier.popular && (
                    <span className="w-fit rounded-full bg-brand-accent px-3 py-1 text-xs font-bold text-background uppercase tracking-wider mb-4">
                      Most Popular
                    </span>
                  )}
                  <h3 className="font-display text-2xl font-bold text-brand-navy">{tier.name}</h3>
                  <div className="mt-3 font-display text-4xl font-extrabold text-brand-navy">
                    {tier.price}
                  </div>
                  <p className="mt-2 text-xs text-brand-navy/60 font-medium">{tier.bestFor}</p>

                  <ul className="mt-6 space-y-3 text-sm text-brand-navy/70 border-t border-brand-navy/10 pt-6 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check
                          className="size-4 text-brand-accent shrink-0 mt-0.5"
                          strokeWidth={2.5}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    search={{ plan: tier.name.toLowerCase(), industry: data.slug }}
                    className={`mt-8 block rounded-xl py-3.5 text-center text-sm font-bold transition-[background-color,border-color,color,transform] duration-150 ease-out active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none ${
                      tier.popular
                        ? "bg-brand-navy text-background hover:bg-brand-accent shadow-xs active:bg-brand-navy/90"
                        : "border border-brand-navy/20 bg-background text-brand-navy hover:bg-brand-surface active:bg-brand-surface/80"
                    }`}
                  >
                    Select {tier.name}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Startup Pricing Callout */}
      <StartupPricingCallout slug={data.slug} />

      {/* 8. Lead Magnet Section */}
      <section className="px-6 py-12">
        <LeadMagnet defaultIndustry={data.title} />
      </section>

      {/* 9. Final CTA */}
      <section className="px-6 py-20 bg-brand-surface/60 border-t border-brand-navy/5 text-center">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold text-brand-navy sm:text-4xl">
              Ready for a clean close and institutional-grade numbers?
            </h2>
            <p className="mt-4 text-lg text-brand-navy/60">
              Schedule a 20-minute scoping diagnostic with our team. We'll review your current setup
              and hand you an actionable plan.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                search={{ industry: data.slug }}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-8 py-4 font-semibold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-brand-accent hover:shadow-xl active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
              >
                {data.ctaText} <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/pricing"
                className="rounded-xl border border-brand-navy/15 bg-background px-8 py-4 font-semibold text-brand-navy transition-[background-color,border-color,transform] duration-150 ease-out hover:bg-brand-surface active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
              >
                View Full Pricing Comparison
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
